/**
 * LegCardBadges — sharp signal and line shopping badge sub-components for LegCard
 */

import { Tooltip } from '@components/ui/Tooltip';
import {
  type BadgeTextContext,
  getArbitrageText,
  getConsensusText,
  getLineMovementText,
  getMiddleText,
  getPositiveEVText,
  getRLMText,
  getSharpLineText,
} from '@parlay-picker/shared/sharpBadgeText';
import type { LegLineShoppingSignals, LegSharpSignals } from '@parlay-picker/shared/sharpMoney';
import { CheckCheck, Layers, Shield, Target, TrendingUp } from 'lucide-react';
import type React from 'react';

// =============================================================================
// SharpSignalBadges
// =============================================================================

interface SharpSignalBadgesProps {
  signals: LegSharpSignals;
  badgeCtx: BadgeTextContext;
}

export function SharpSignalBadges({
  signals,
  badgeCtx,
}: SharpSignalBadgesProps): React.ReactElement {
  const wentUp = (signals.lineMovement ?? 0) > 0;

  return (
    <>
      {signals.sharpConsensus &&
        (() => {
          const t = getConsensusText(badgeCtx);
          return (
            <Tooltip
              content={
                <>
                  <span className="font-semibold text-yellow-300">{t.title}</span>
                  <br />
                  {t.body}
                </>
              }
            >
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-yellow-500/20 text-yellow-300 ring-1 ring-yellow-500/30 cursor-help">
                <CheckCheck className="w-3 h-3" />
                2/2 Sharp
              </span>
            </Tooltip>
          );
        })()}
      {!signals.sharpConsensus &&
        signals.sharpBookSource &&
        signals.sharpLine != null &&
        (() => {
          const t = getSharpLineText(badgeCtx);
          return (
            <Tooltip
              content={
                <>
                  <span className="font-semibold text-slate-300">{t.title}</span>
                  <br />
                  {t.body}
                </>
              }
            >
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-500/20 text-slate-300 ring-1 ring-slate-500/30 cursor-help">
                <Target className="w-3 h-3" />
                {signals.sharpBookSource === 'both' ? '1/2 Sharp' : '1 Sharp'}
              </span>
            </Tooltip>
          );
        })()}
      {signals.isPositiveEV &&
        (() => {
          const t = getPositiveEVText(badgeCtx);
          return (
            <Tooltip
              content={
                <>
                  <span className="font-semibold text-emerald-400">{t.title}</span>
                  <br />
                  {t.body}
                </>
              }
            >
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-500/20 text-emerald-400 ring-1 ring-emerald-500/30 cursor-help">
                <TrendingUp className="w-3 h-3" />
                +EV
                {signals.evPercent != null ? ` ${signals.evPercent.toFixed(1)}%` : ''}
              </span>
            </Tooltip>
          );
        })()}
      {signals.isReverseLineMovement &&
        (() => {
          const t = getRLMText(badgeCtx);
          return (
            <Tooltip
              content={
                <>
                  <span className="font-semibold text-amber-400">{t.title}</span>
                  <br />
                  {t.body}
                </>
              }
            >
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-500/20 text-amber-400 ring-1 ring-amber-500/30 cursor-help">
                <Target className="w-3 h-3" />
                RLM
              </span>
            </Tooltip>
          );
        })()}
      {signals.lineMovement != null &&
        Math.abs(signals.lineMovement) >= 0.5 &&
        (() => {
          const t = getLineMovementText(badgeCtx);
          return (
            <Tooltip
              content={
                <>
                  <span className="font-semibold text-blue-400">{t.title}</span>
                  <br />
                  {t.body}
                </>
              }
            >
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-500/20 text-blue-400 ring-1 ring-blue-500/30 cursor-help">
                Line {wentUp ? '↑' : '↓'}
                {Math.abs(signals.lineMovement!).toFixed(1)}
              </span>
            </Tooltip>
          );
        })()}
    </>
  );
}

// =============================================================================
// LineShoppingBadges
// =============================================================================

interface LineShoppingBadgesProps {
  shopping: LegLineShoppingSignals;
  badgeCtx: BadgeTextContext;
}

export function LineShoppingBadges({
  shopping,
  badgeCtx,
}: LineShoppingBadgesProps): React.ReactElement {
  return (
    <>
      {shopping.hasMiddleOpportunity &&
        (() => {
          const t = getMiddleText(badgeCtx, shopping);
          return (
            <Tooltip
              content={
                <>
                  <span className="font-semibold text-violet-400">{t.title}</span>
                  <br />
                  {t.body}
                  {shopping.middleSize != null && (
                    <>
                      <br />
                      <br />
                      <span className="text-violet-300 font-semibold">
                        {shopping.middleSize.toFixed(1)}pt gap
                      </span>
                    </>
                  )}
                </>
              }
            >
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-violet-500/20 text-violet-400 ring-1 ring-violet-500/30 cursor-help">
                <Layers className="w-3 h-3" />
                Middle {shopping.middleSize != null ? `${shopping.middleSize.toFixed(1)}pt` : ''}
              </span>
            </Tooltip>
          );
        })()}
      {shopping.hasArbitrageOpportunity &&
        (() => {
          const t = getArbitrageText(badgeCtx, shopping);
          return (
            <Tooltip
              content={
                <>
                  <span className="font-semibold text-teal-400">{t.title}</span>
                  <br />
                  {t.body}
                  {shopping.arbitrageProfitPercent != null && (
                    <>
                      <br />
                      <br />
                      <span className="text-teal-300 font-semibold">
                        {shopping.arbitrageProfitPercent.toFixed(1)}% guaranteed profit
                      </span>
                    </>
                  )}
                </>
              }
            >
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-teal-500/20 text-teal-400 ring-1 ring-teal-500/30 cursor-help">
                <Shield className="w-3 h-3" />
                Arb{' '}
                {shopping.arbitrageProfitPercent != null
                  ? `${shopping.arbitrageProfitPercent.toFixed(1)}%`
                  : ''}
              </span>
            </Tooltip>
          );
        })()}
    </>
  );
}
